import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { FinancialStatementModel } from "../../entities/financialStatement";
import { apiSlice, TagTypes } from "./apiSlice";
import { FsType } from "../../entities/enums";
import { getQuerySearchParams } from "../../utils";

export const financialStatementApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFinancialStatementCommonInfo: builder.query<EntityCommonInfoModel, FsType>({
      query: (fsType: FsType) => `/financialStatement/${fsType}/commonInfo`,
      providesTags: [{ type: TagTypes.FinancialStatement, id: "LIST" }],
    }),
    getFinancialStatementFilteredPaged: builder.query<FilteredPagedQueryResult<FinancialStatementModel>, FilteredPagedModel>({
      query: ({ fsType, page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/financialStatement/${fsType}/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.FinancialStatement, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.FinancialStatement,
                id,
              })),
            ]
          : [{ type: TagTypes.FinancialStatement, id: "PARTIAL-LIST" }],
    }),
    getFinancialStatement: builder.query<FinancialStatementModel, number>({
      query: (financialStatementId) => `/financialStatement/getById/${financialStatementId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.FinancialStatement, id: arg }],
    }),
    scrapFinancialStatement: builder.query<FinancialStatementModel, { fsType: FsType; tracingNumber: string }>({
      query: ({ fsType, tracingNumber }) => ({
        url: `/financialStatement/scrap/${fsType}`,
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.FinancialStatement, id: "Scrap" }],
    }),
    addFinancialStatement: builder.mutation({
      query: (initialFinancialStatement) => ({
        url: "/financialStatement",
        method: "POST",
        body: initialFinancialStatement,
      }),
      invalidatesTags: [
        { type: TagTypes.FinancialStatement, id: "LIST" },
        { type: TagTypes.FinancialStatement, id: "PARTIAL-LIST" },
      ],
    }),
    editFinancialStatement: builder.mutation({
      query: (financialStatement) => ({
        url: `/financialStatement/${financialStatement.id}`,
        method: "PUT",
        body: financialStatement,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.FinancialStatement, id: arg.id }],
    }),
    deleteFinancialStatement: builder.mutation({
      query: (financialStatementId) => ({
        url: `/financialStatement/${financialStatementId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.FinancialStatement, id: arg },
              { type: TagTypes.FinancialStatement, id: "LIST" },
            ],
    }),
    deleteMultiFinancialStatements: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/financialStatement/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.FinancialStatement,
                id,
              })),
              { type: TagTypes.FinancialStatement, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetFinancialStatementCommonInfoQuery,
  useGetFinancialStatementFilteredPagedQuery,
  useGetFinancialStatementQuery,
  useScrapFinancialStatementQuery,
  useLazyScrapFinancialStatementQuery,
  useAddFinancialStatementMutation,
  useEditFinancialStatementMutation,
  useDeleteFinancialStatementMutation,
  useDeleteMultiFinancialStatementsMutation,
} = financialStatementApi;
